import {
  Group,
  Stack,
  Text,
  TextInput,
  Textarea,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { IconEdit, IconCheck, IconX } from "@tabler/icons-react";
import { useState } from "react";

export default function EditableText({
  value,
  fieldName,
  editingField,
  setEditingField,
  onSave,
  multiline = false,
  placeholder = "Click to edit",
  disabled = false,
  hint,
  softLimit,
}) {
  const [localValue, setLocalValue] = useState(value || "");
  const isEditing = editingField === fieldName;

  // The draft is seeded when editing starts, not synced from the prop. The brand is refetched
  // while discovery runs and after "Re-analyze brand"; a sync effect would drop whatever the
  // user had typed by then, which is a long wait to lose on a field this size.
  const startEditing = () => {
    if (disabled) return;
    setLocalValue(value || "");
    setEditingField(fieldName);
  };

  const handleSave = () => {
    onSave(fieldName, localValue);
  };

  const handleCancel = () => {
    setEditingField(null);
    setLocalValue(value || "");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    } else if (e.key === "Enter" && (!multiline || e.metaKey || e.ctrlKey)) {
      // In a textarea Enter inserts a newline, so only the modifier combo commits.
      e.preventDefault();
      handleSave();
    }
  };

  if (isEditing) {
    const Component = multiline ? Textarea : TextInput;
    const sizing = multiline
      ? { autosize: true, minRows: 4, maxRows: 24 }
      : {};
    const keyHint = multiline
      ? `${hint ? hint + " " : ""}Cmd/Ctrl+Enter saves, Esc cancels.`
      : hint;

    return (
      <Stack gap={4} w="100%">
        <Group gap="xs" w="100%" wrap="nowrap" align="flex-start">
          <Component
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            style={{ flex: 1 }}
            autoFocus
            {...sizing}
          />
          <ActionIcon color="green" onClick={handleSave} aria-label="Save">
            <IconCheck size={16} />
          </ActionIcon>
          <ActionIcon color="red" onClick={handleCancel} aria-label="Cancel">
            <IconX size={16} />
          </ActionIcon>
        </Group>
        {(keyHint || softLimit > 0) && (
          <Group gap="md" justify="space-between" wrap="nowrap" align="flex-start">
            <Text size="xs" c="dimmed">
              {keyHint}
            </Text>
            {softLimit > 0 && (
              <Text
                size="xs"
                c={localValue.length > softLimit ? "orange" : "dimmed"}
                style={{ whiteSpace: "nowrap" }}
              >
                {localValue.length} / {softLimit}
              </Text>
            )}
          </Group>
        )}
      </Stack>
    );
  }

  return (
    <Group
      gap="xs"
      w="100%"
      wrap="nowrap"
      align={multiline ? "flex-start" : "center"}
    >
      <Text
        style={{
          flex: 1,
          cursor: disabled ? "default" : "pointer",
          // Long-form values are stored verbatim and fed to prompts verbatim, so show the
          // line breaks the user typed instead of collapsing them into one paragraph.
          whiteSpace: multiline ? "pre-wrap" : undefined,
          maxHeight: multiline ? 240 : undefined,
          overflowY: multiline ? "auto" : undefined,
        }}
        onClick={startEditing}
        c={value ? undefined : "dimmed"}
        fs={multiline && value ? "italic" : undefined}
      >
        {value || placeholder}
      </Text>
      {!disabled && (
        <Tooltip label="Click to edit">
          <ActionIcon size="sm" variant="subtle" onClick={startEditing}>
            <IconEdit size={14} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
}
